/**
 * Module color palettes aligned to the Silver Fern Brand Guide.
 *
 * Each module has a 4-level palette:
 *   primary  — the signature brand color (used in nav, headers, accents)
 *   darker   — deeper shade for contrast / hover states
 *   desat1   — desaturated mid-tone for backgrounds, borders
 *   desat2   — very light tint for surface / row-stripe backgrounds
 *
 * Brand guide source: Silver Fern Brand Guide (app icon palettes).
 * Audit ref: [1b] — Intelligence and Reports primary colors corrected.
 */

interface ModulePalette {
	darker: string;
	desat1: string;
	desat2: string;
	primary: string;
}

type ModuleColorKey =
	| "AVAILABILITY"
	| "BENCHMARK"
	| "COUNT"
	| "ENGAGE"
	| "EVENTS"
	| "FORECAST"
	| "FULFILL"
	| "INTELLIGENCE"
	| "MATERIAL"
	| "PORTAL"
	| "PRODUCE"
	| "REPORTS"
	| "RESTOCK"
	| "VERIFY"
	| "WORKSUITE";

export const modulePalettes: Record<ModuleColorKey, ModulePalette> = {
	AVAILABILITY: {
		darker: "#136E83",
		desat1: "#D0EBF1",
		desat2: "#F0F8FA",
		primary: "#1A93AE"
	},
	BENCHMARK: {
		darker: "#1E5650",
		desat1: "#D3E7E5",
		desat2: "#F1F8F7",
		primary: "#2B7A72"
	},
	COUNT: {
		darker: "#A3822A",
		desat1: "#F5ECD3",
		desat2: "#FCF8EE",
		primary: "#D8AD37"
	},
	ENGAGE: {
		darker: "#5B6E4F",
		desat1: "#E2EADC",
		desat2: "#F5F8F3",
		primary: "#7A936A"
	},
	EVENTS: {
		darker: "#1F3354",
		desat1: "#D4DCE8",
		desat2: "#F1F4F8",
		primary: "#2B4570"
	},
	FORECAST: {
		darker: "#4B3456",
		desat1: "#E8E0EC",
		desat2: "#F8F5F9",
		primary: "#644672"
	},
	FULFILL: {
		darker: "#611C1C",
		desat1: "#F1D6D6",
		desat2: "#FAF2F2",
		primary: "#822525"
	},
	INTELLIGENCE: {
		darker: "#446983",
		desat1: "#DFEAF1",
		desat2: "#F5F8FA",
		primary: "#6296BC"
	},
	MATERIAL: {
		darker: "#730000",
		desat1: "#F0CCCC",
		desat2: "#FAF0F0",
		primary: "#990000"
	},
	PORTAL: {
		darker: "#04689A",
		desat1: "#CCE7F5",
		desat2: "#EFF8FC",
		primary: "#068ACD"
	},
	PRODUCE: {
		darker: "#994D0E",
		desat1: "#F7E2D0",
		desat2: "#FCF6F0",
		primary: "#DB6E14"
	},
	REPORTS: {
		darker: "#A27B18",
		desat1: "#FAEFD3",
		desat2: "#FDFAF1",
		primary: "#E8B023"
	},
	RESTOCK: {
		darker: "#45804A",
		desat1: "#DFF0E1",
		desat2: "#F5FAF6",
		primary: "#63B76B"
	},
	VERIFY: {
		darker: "#4E333D",
		desat1: "#E5D9DD",
		desat2: "#F6F2F3",
		primary: "#684551"
	},
	WORKSUITE: {
		darker: "#4E6E50",
		desat1: "#E1EBE2",
		desat2: "#F5F8F5",
		primary: "#69936C"
	}
};

/**
 * Flat map of module → primary color.
 * This is the default export for backward compatibility — existing consumers
 * that only need `moduleColors.FULFILL` (the primary) continue to work unchanged.
 */
const moduleColors: Record<ModuleColorKey, string> = Object.fromEntries(
	Object.entries(modulePalettes).map(([key, palette]) => [key, palette.primary])
) as Record<ModuleColorKey, string>;

export const productModules = ["FULFILL", "PRODUCE", "RESTOCK", "FORECAST", "PORTAL", "REPORTS"];

export type { ModuleColorKey, ModulePalette };
export default moduleColors;
